import { useCallback, useEffect, useRef, useState } from 'react';
import type { AcceptGarmentResult } from './useCatcherMutations';

export interface PlacementDestination {
  variant: 'bin' | 'shelf' | 'bench';
  binLabel: string;
}

export interface AcceptedPlacement {
  destination: PlacementDestination;
  result: AcceptGarmentResult;
}

/** Outcome of an accept resolving against the current preview/confirmation state. */
export type PlacementResolution = 'completed' | 'rearmed' | 'stale';

/** Preview an assigned container while saving, but only complete after the audited accept. */
export function useCatcherPlacement({
  onComplete,
  onPrePrint,
}: {
  onComplete: (placement: AcceptedPlacement) => void;
  onPrePrint: () => void;
}) {
  const [placement, setPlacement] = useState<PlacementDestination | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const placementRef = useRef<PlacementDestination | null>(null);
  const requestRef = useRef<{ token: symbol; confirmed: PlacementDestination | null } | null>(null);
  const acceptedRef = useRef<AcceptedPlacement | null>(null);
  // Latest callbacks by ref so begin/confirm/resolve/fail keep stable identity
  // across renders — PlacementOverlay's document keydown effect re-registers
  // whenever any of these change, which would otherwise happen every render.
  const optionsRef = useRef({ onComplete, onPrePrint });
  optionsRef.current = { onComplete, onPrePrint };

  const reset = useCallback(() => {
    requestRef.current = null;
    acceptedRef.current = null;
    placementRef.current = null;
    setPlacement(null);
    setIsSaving(false);
    setIsConfirmed(false);
  }, []);

  // A late response must not resurrect a station that has been left.
  useEffect(() => () => { requestRef.current = null; }, []);

  const begin = useCallback((preview: PlacementDestination | null = null) => {
    // Synchronous lock covers repeated keys/clicks before React can render busy state.
    if (requestRef.current || placementRef.current) return null;
    const token = Symbol('catcher-accept');
    requestRef.current = { token, confirmed: null };
    placementRef.current = preview;
    setPlacement(preview);
    setIsSaving(true);
    setIsConfirmed(false);
    return token;
  }, []);

  const confirm = useCallback(() => {
    if (requestRef.current) {
      if (!placementRef.current || requestRef.current.confirmed) return;
      requestRef.current.confirmed = placementRef.current;
      setIsConfirmed(true);
      return;
    }
    const accepted = acceptedRef.current;
    if (!accepted) return;
    reset();
    optionsRef.current.onComplete(accepted);
  }, [reset]);

  const resolve = useCallback((token: symbol, result: AcceptGarmentResult, isBench = false): PlacementResolution => {
    if (requestRef.current?.token !== token) return 'stale';
    const confirmed = requestRef.current.confirmed;
    reset();

    if (result.isPrePrintBatch) {
      optionsRef.current.onPrePrint();
      return 'completed';
    }

    const destination: PlacementDestination = result.binLabel
      ? { variant: isBench ? 'bench' : 'bin', binLabel: result.binLabel }
      : { variant: 'shelf', binLabel: '' };
    const accepted = { destination, result };
    // A preview confirmation applies only to the destination the server actually saved.
    if (confirmed?.variant === destination.variant && confirmed.binLabel === destination.binLabel) {
      optionsRef.current.onComplete(accepted);
      return 'completed';
    }
    placementRef.current = destination;
    acceptedRef.current = accepted;
    setPlacement(destination);
    return 'rearmed';
  }, [reset]);

  const fail = useCallback((token: symbol) => {
    if (requestRef.current?.token !== token) return false;
    reset();
    return true;
  }, [reset]);

  return { placement, isSaving, isConfirmed, begin, confirm, resolve, fail, reset };
}
